import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { LINE_STATUSES } from '../firebase/schema';
import { comparePrices, placeOrders, testSanMarConnection } from '../services/supplierApi';

/**
 * SanMar getPricing expects COLOR_NAME from their catalog (e.g. "Black", "White").
 * Map common abbreviations/variants to SanMar's expected color string.
 */
const SANMAR_COLOR_MAP = {
  BLK: 'Black',
  WHT: 'White',
  WH: 'White',
  NAV: 'Navy',
  NAVY: 'Navy',
  RED: 'Red',
  ROYAL: 'Royal Blue',
  'ROYAL BLUE': 'Royal Blue',
  ASH: 'Ash',
  'HEATHER GREY': 'Heather Grey',
  'HEATHER GRAY': 'Heather Grey',
  GREY: 'Heather Grey',
  GRAY: 'Heather Grey',
  NATURAL: 'Natural',
  KELLY: 'Kelly Green',
  'KELLY GREEN': 'Kelly Green',
  FOREST: 'Forest Green',
  'FOREST GREEN': 'Forest Green',
  'ATHLETIC HEATHER': 'Athletic Heather',
  'BLACK HEATHER': 'Black Heather',
  'HEATHER ICE BLUE': 'Heather Ice Blue',
  AQUA: 'Aqua',
  ARMY: 'Army',
  BURGUNDY: 'Burgundy',
  'CAROLINA BLUE': 'Carolina Blue',
  CHARCOAL: 'Charcoal',
  GOLD: 'Gold',
  ORANGE: 'Orange',
  PINK: 'Pink',
  PURPLE: 'Purple',
  SANGRIA: 'Sangria',
  SILVER: 'Silver',
  YELLOW: 'Yellow',
  'MARITIME FROST': 'Maritime Frost',
  'MARITIME': 'Maritime Frost',
  'HEATHER NATURAL': 'Heather Natural',
  'HEATHER BABY BLUE': 'Heather Baby Blue',
  'BABY BLUE': 'Heather Baby Blue',
};

function normalizeColorForSanMar(color) {
  if (!color || typeof color !== 'string') return '';
  const trimmed = color.trim();
  if (!trimmed) return '';
  const upper = trimmed.toUpperCase().replace(/\s+/g, ' ');
  const mapped = SANMAR_COLOR_MAP[upper] || SANMAR_COLOR_MAP[trimmed];
  if (mapped) return mapped;
  return trimmed;
}

/** Enrich lines for SanMar lookup: style from product.sanmarStyleId, color from line (normalized to SanMar COLOR_NAME). */
function enrichLinesForSanMar(lines) {
  return lines.map(line => ({
    ...line,
    garmentColorSanMar: normalizeColorForSanMar(line.garmentColor || ''),
  }));
}

const PageContainer = styled.div`
  padding: 20px;
  padding-top: 100px; /* Space for fixed navbar */
  max-width: 1800px;
  margin: 0 auto;
  background-color: var(--al-bg);
  min-height: 100vh;
  font-family: 'Montserrat', sans-serif;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid rgba(var(--al-primary-rgb), 0.3);
`;

const Title = styled.h1`
  color: #FFFFFF;
  font-size: 2rem;
  margin: 0;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const BetaBadge = styled.span`
  background: var(--al-primary);
  color: var(--al-bg);
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusIndicators = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(var(--al-primary-rgb), 0.3);
  }
`;

const StatusDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => {
    if (props.$status === 'connected') return 'var(--al-primary)';
    if (props.$status === 'error') return '#ff6b6b';
    if (props.$status === 'testing') return '#ffd93d';
    return '#888';
  }};
  box-shadow: ${props => {
    if (props.$status === 'connected') return '0 0 8px var(--al-primary)';
    if (props.$status === 'error') return '0 0 8px #ff6b6b';
    if (props.$status === 'testing') return '0 0 8px #ffd93d';
    return 'none';
  }};
  animation: ${props => props.$status === 'testing' ? 'pulse 1.5s infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

const StatusLabel = styled.span`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const SampleGarmentLine = styled.div`
  font-size: 0.75rem;
  color: rgba(var(--al-primary-rgb), 0.9);
  margin-top: 4px;
  font-weight: 500;
`;

const SanMarStatusBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const StatsBar = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: var(--al-surface);
  padding: 20px;
  border-radius: 8px;
  border: 1px solid rgba(var(--al-primary-rgb), 0.2);
  
  h3 {
    margin: 0 0 8px 0;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.7);
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-size: 1.75rem;
    font-weight: 700;
    color: #FFFFFF;
  }
`;

const SavingsCard = styled(StatCard)`
  background: linear-gradient(135deg, var(--al-primary) 0%, #7FE07F 100%);
  border-color: var(--al-primary);
  
  h3, p {
    color: var(--al-bg);
  }
`;

const ControlsBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: 'Montserrat', sans-serif;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: var(--al-primary);
  color: var(--al-bg);
  
  &:hover:not(:disabled) {
    background: #7FE07F;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--al-primary-rgb), 0.3);
  }
`;

const SecondaryButton = styled(Button)`
  background: var(--al-surface);
  color: var(--al-primary);
  border: 1px solid var(--al-primary);
  
  &:hover:not(:disabled) {
    background: rgba(var(--al-primary-rgb), 0.1);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--al-primary-rgb), 0.2);
  }
`;

const OutlineButton = styled(Button)`
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.3);
  
  &:hover:not(:disabled) {
    border-color: var(--al-primary);
    color: var(--al-primary);
    background: rgba(var(--al-primary-rgb), 0.05);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--al-surface);
  border: 2px solid var(--al-primary);
  border-radius: 8px;
  padding: 32px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 8px 32px rgba(var(--al-primary-rgb), 0.2);
`;

const ModalTitle = styled.h2`
  color: var(--al-primary);
  font-size: 1.5rem;
  margin: 0 0 20px 0;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ModalText = styled.p`
  color: #FFFFFF;
  font-size: 1rem;
  line-height: 1.6;
  margin: 12px 0;
  
  &.highlight {
    color: var(--al-primary);
    font-weight: 600;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const DangerButton = styled(Button)`
  background: #FF6B6B;
  color: #FFFFFF;
  
  &:hover:not(:disabled) {
    background: #FF5252;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  }
`;

const TableContainer = styled.div`
  background: var(--al-surface);
  border-radius: 8px;
  border: 1px solid rgba(var(--al-primary-rgb), 0.2);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: var(--al-bg);
  border-bottom: 2px solid rgba(var(--al-primary-rgb), 0.3);
`;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    background: rgba(var(--al-primary-rgb), 0.05);
  }
`;

const TableCell = styled.td`
  padding: 12px;
  font-size: 0.85rem;
  color: #FFFFFF;
  vertical-align: middle;
`;

const TableHeaderCell = styled.th`
  padding: 16px 12px;
  text-align: left;
  font-size: 0.75rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--al-primary);
`;

const PriceCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Price = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: ${props => props.isBest ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.7)'};
  
  ${props => props.isBest && `
    &::before {
      content: '✓ ';
      color: var(--al-primary);
      font-weight: 700;
    }
  `}
`;

const PriceLabel = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
`;

const SavingsTag = styled.span`
  background: var(--al-primary);
  color: var(--al-bg);
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-block;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SupplierSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid rgba(var(--al-primary-rgb), 0.3);
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  background: var(--al-bg);
  color: #FFFFFF;
  font-family: 'Montserrat', sans-serif;
  text-transform: uppercase;
  font-weight: 600;
  
  &:focus {
    outline: none;
    border-color: var(--al-primary);
    background: var(--al-surface);
  }
  
  option {
    background: var(--al-bg);
    color: #FFFFFF;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.125rem;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: var(--al-surface);
  border-radius: 8px;
  border: 1px solid rgba(var(--al-primary-rgb), 0.2);
  
  h3 {
    color: var(--al-primary);
    margin-bottom: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 1rem;
  }
`;

const APIOrdering = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [linesByOrder, setLinesByOrder] = useState({});
  const [selectedLines, setSelectedLines] = useState({});
  const [priceComparisons, setPriceComparisons] = useState({});
  const [supplierSelections, setSupplierSelections] = useState({});
  const [apiStatus, setApiStatus] = useState({
    ssActivewear: { status: 'unknown', message: 'Not tested' },
    sanmar: { status: 'unknown', message: 'Not tested' }
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [testingSanMar, setTestingSanMar] = useState(false);

  // Test SanMar API connection (explicit check) + sample garment price (BC3001 Black)
  const handleTestSanMarConnection = async () => {
    setTestingSanMar(true);
    setApiStatus(prev => ({
      ...prev,
      sanmar: { status: 'testing', message: 'Testing...' }
    }));
    try {
      const result = await testSanMarConnection();
      const envLabel = result.environment === 'test' ? ' (Test WSDL)' : result.environment === 'production' ? ' (Production)' : '';
      const sampleGarment = result.data?.sampleGarment || null;
      setApiStatus(prev => ({
        ...prev,
        sanmar: result.success
          ? { status: 'connected', message: `Connected${envLabel}`, sampleGarment }
          : { status: 'error', message: `${result.error || result.message || 'Connection failed'}${envLabel}`.trim() }
      }));
      if (!result.success) {
        console.warn('SanMar connection test failed:', result.error || result.message, { environment: result.environment, useTest: result.useTest });
      }
    } catch (error) {
      setApiStatus(prev => ({
        ...prev,
        sanmar: { status: 'error', message: error.message || 'Test failed' }
      }));
    } finally {
      setTestingSanMar(false);
    }
  };

  // Load orders and lines
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update API status based on pricing results (preserves manual "Test SanMar" result when no SanMar data in comparisons)
  const updateApiStatusFromResults = (comparisons) => {
    let ssHasSuccess = false;
    let ssHasError = false;
    let ssHasMappingIssues = false;
    let sanmarHasSuccess = false;
    let sanmarHasError = false;

    comparisons.forEach(comp => {
      if (comp.suppliers?.ssActivewear?.total) {
        ssHasSuccess = true;
      } else if (comp.suppliers?.ssActivewear?.error) {
        if (comp.errors?.some(e => e.includes('No supplier mapping'))) {
          ssHasMappingIssues = true;
        } else {
          ssHasError = true;
        }
      } else if (comp.suppliers?.ssActivewear?.available === false) {
        ssHasMappingIssues = true;
      }

      if (comp.suppliers?.sanmar?.total) {
        sanmarHasSuccess = true;
      } else if (comp.suppliers?.sanmar?.error) {
        sanmarHasError = true;
      }
    });

    setApiStatus(prev => {
      const ssStatus = {
        status: ssHasSuccess ? 'connected' : (ssHasError ? 'error' : (ssHasMappingIssues ? 'error' : 'unknown')),
        message: ssHasSuccess ? 'Connected' : (ssHasError ? 'API Error' : (ssHasMappingIssues ? 'Add Style IDs' : 'No data'))
      };
      // Only update SanMar from results when we have SanMar data in comparisons; otherwise keep manual test result
      const sanmarStatus = sanmarHasSuccess
        ? { ...prev.sanmar, status: 'connected', message: 'Connected' }
        : sanmarHasError
          ? { ...prev.sanmar, status: 'error', message: prev.sanmar?.message || 'API Error' }
          : prev.sanmar;

      const newStatus = {
        ssActivewear: ssStatus,
        sanmar: sanmarStatus
      };
      console.log('📊 API Status Update:', {
        ssHasSuccess,
        ssHasError,
        ssHasMappingIssues,
        sanmarHasSuccess,
        sanmarHasError,
        newStatus
      });
      return newStatus;
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Query orders that are ready to order
      const ordersQuery = query(
        collection(db, 'orders'),
        where('status', '==', 'Ready To Order')
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log('📦 Found orders with "Ready To Order" status:', ordersData.length);

      // Load lines for each order
      const linesMap = {};
      
      for (const order of ordersData) {
        const linesQuery = query(
          collection(db, 'orderLines'),
          where('orderId', '==', order.id)
          // Not filtering by status - get all lines for these orders
        );
        
        const linesSnapshot = await getDocs(linesQuery);
        const lines = linesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Filter lines to only those that are ready to order (not already ordered, not completed)
        const readyLines = lines.filter(line => {
          const status = line.status || '';
          const isReadyToOrder = status === 'Ready To Order' || status === LINE_STATUSES.READY_TO_ORDER;
          const isNew = status === 'New' || status === LINE_STATUSES.NEW;
          // Include lines that are either Ready To Order or New
          return isReadyToOrder || isNew;
        });
        
        console.log(`📋 Order ${order.customerName}:`, lines.length, 'total lines,', readyLines.length, 'ready to order');
        console.log('  Line statuses:', lines.map(l => l.status));
        
        if (readyLines.length > 0) {
          linesMap[order.id] = readyLines;
        }
      }

      // Filter orders to only those with READY_TO_ORDER lines
      const ordersWithReadyLines = ordersData.filter(order => linesMap[order.id]?.length > 0);

      setOrders(ordersWithReadyLines);
      setLinesByOrder(linesMap);
      
      // Auto-load REAL price comparisons
      loadRealPriceComparisons(linesMap);
      
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Load REAL price comparisons from Firebase Function
  const loadRealPriceComparisons = async (linesMap) => {
    try {
      // Flatten all lines from all orders
      const allLines = Object.values(linesMap).flat();
      
      if (allLines.length === 0) {
        setPriceComparisons({});
        setSupplierSelections({});
        return;
      }
      
      console.log('🚀 Calling REAL comparePrices API with', allLines.length, 'lines');

      // Enrich lines for SanMar: normalized color (COLOR_NAME) so getPricing(style, color) matches catalog
      const linesToSend = enrichLinesForSanMar(allLines);

      // Call the real Firebase Function (S&S unchanged; SanMar uses garmentColorSanMar when present)
      const result = await comparePrices(linesToSend);
      
      console.log('✅ Price comparison result:', result);
      
      // Convert array of comparisons to object keyed by lineId
      const comparisons = {};
      const selections = {};
      
      result.comparisons.forEach(comp => {
        console.log(`📦 Line ${comp.lineId} (${comp.garmentId}):`, {
          hasSSPrice: !!comp.suppliers?.ssActivewear?.total,
          ssPrice: comp.suppliers?.ssActivewear?.total,
          hasSanMarPrice: !!comp.suppliers?.sanmar?.total,
          errors: comp.errors,
          bestSupplier: comp.bestSupplier,
          ssData: comp.suppliers?.ssActivewear
        });
        
        // Show any errors
        if (comp.errors && comp.errors.length > 0) {
          console.warn(`⚠️ Errors for line ${comp.lineId}:`, comp.errors);
        }
        
        comparisons[comp.lineId] = comp;
        // Auto-select best price, or 'manual' if no suppliers available
        selections[comp.lineId] = comp.bestSupplier || 'manual';
      });
      
      setPriceComparisons(comparisons);
      setSupplierSelections(selections);
      
      // Update API status based on results
      updateApiStatusFromResults(result.comparisons);
      
      // Show success message
      console.log(`✅ Price comparison complete for ${result.comparisons.length} lines`);
      
    } catch (error) {
      console.error('❌ Error loading price comparisons:', error);
      alert(`Failed to load price comparisons: ${error.message}`);
    }
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const allLines = Object.values(linesByOrder).flat();
    const selectedCount = Object.values(selectedLines).filter(Boolean).length;
    
    let estimatedTotal = 0;
    let totalPieces = 0;
    let sanMarCount = 0;
    let ssCount = 0;
    let manualCount = 0;
    
    allLines.forEach(line => {
      if (selectedLines[line.id]) {
        const comparison = priceComparisons[line.id];
        if (comparison) {
          const supplier = supplierSelections[line.id];
          
          // Add to estimated total based on selected supplier
          if (supplier === 'ssActivewear' && comparison.suppliers?.ssActivewear?.total) {
            estimatedTotal += comparison.suppliers.ssActivewear.total;
          } else if (supplier === 'sanmar' && comparison.suppliers?.sanmar?.total) {
            estimatedTotal += comparison.suppliers.sanmar.total;
          }
          
          // Count pieces
          totalPieces += line.totalQuantity || 0;
          
          // Count by supplier
          if (supplier === 'sanmar') sanMarCount++;
          if (supplier === 'ssActivewear') ssCount++;
          if (supplier === 'manual') manualCount++;
        }
      }
    });
    
    return {
      totalLines: allLines.length,
      selectedCount,
      estimatedTotal,
      totalPieces,
      sanMarCount,
      ssCount,
      manualCount
    };
  }, [linesByOrder, selectedLines, priceComparisons, supplierSelections]);

  // Handle select all
  const handleSelectAll = (checked) => {
    const allLines = Object.values(linesByOrder).flat();
    const newSelections = {};
    allLines.forEach(line => {
      newSelections[line.id] = checked;
    });
    setSelectedLines(newSelections);
  };

  // Handle place orders - show confirmation modal
  const handlePlaceOrders = () => {
    if (stats.selectedCount === 0) {
      alert('Please select at least one line to order');
      return;
    }
    
    setShowConfirmModal(true);
  };

  // Actually place the orders after confirmation
  const confirmPlaceOrders = async () => {
    setPlacingOrder(true);
    
    try {
      // Collect selected lines with their supplier selections
      const linesToPlace = Object.values(linesByOrder)
        .flat()
        .filter(line => selectedLines[line.id])
        .map(line => ({
          ...line,
          supplier: supplierSelections[line.id]
        }));
      
      console.log('📦 Placing orders for lines:', linesToPlace);
      
      // Call the placeOrders Firebase Function
      const result = await placeOrders(linesToPlace);
      
      console.log('✅ Orders placed successfully:', result);
      
      alert(`Orders placed successfully!\n\n${result.summary || 'Check Firebase console for details'}`);
      
      // Reload the data to refresh order statuses
      await loadData();
      
      // Clear selections
      setSelectedLines({});
      setShowConfirmModal(false);
      
    } catch (error) {
      console.error('❌ Error placing orders:', error);
      alert(`Failed to place orders: ${error.message}`);
    } finally {
      setPlacingOrder(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingMessage>Loading orders...</LoadingMessage>
      </PageContainer>
    );
  }

  const allLinesCount = Object.values(linesByOrder).flat().length;

  if (allLinesCount === 0) {
    return (
      <PageContainer>
        <PageHeader>
          <div>
            <Title>
              API Ordering
              <BetaBadge>BETA</BetaBadge>
            </Title>
          </div>
          <StatusIndicators>
            <StatusIndicator title={`S&S Activewear: ${apiStatus.ssActivewear.message}`}>
              <StatusDot $status={apiStatus.ssActivewear.status} />
              <StatusLabel>S&S: {apiStatus.ssActivewear.message}</StatusLabel>
            </StatusIndicator>
            <SanMarStatusBlock>
              <StatusIndicator title={`SanMar: ${apiStatus.sanmar.message}`}>
                <StatusDot $status={apiStatus.sanmar.status} />
                <StatusLabel>SanMar: {apiStatus.sanmar.message}</StatusLabel>
              </StatusIndicator>
              {apiStatus.sanmar.sampleGarment?.sampleUnitPrice != null && (
                <SampleGarmentLine>
                  Sample: {apiStatus.sanmar.sampleGarment.label} — ${Number(apiStatus.sanmar.sampleGarment.sampleUnitPrice).toFixed(2)}/unit
                  {apiStatus.sanmar.sampleGarment.sampleSize && ` (${apiStatus.sanmar.sampleGarment.sampleSize})`}
                </SampleGarmentLine>
              )}
            </SanMarStatusBlock>
            <OutlineButton onClick={handleTestSanMarConnection} disabled={testingSanMar}>
              {testingSanMar ? 'Testing...' : 'Test SanMar'}
            </OutlineButton>
          </StatusIndicators>
        </PageHeader>
        <EmptyMessage>
          <h3>No Lines Ready to Order</h3>
          <p>Lines must be in "READY TO ORDER" status to appear here.</p>
        </EmptyMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <div>
          <Title>
            API Ordering
            <BetaBadge>BETA</BetaBadge>
          </Title>
        </div>
        <StatusIndicators>
          <StatusIndicator title={`S&S Activewear: ${apiStatus.ssActivewear.message}`}>
            <StatusDot $status={apiStatus.ssActivewear.status} />
            <StatusLabel>S&S: {apiStatus.ssActivewear.message}</StatusLabel>
          </StatusIndicator>
          <SanMarStatusBlock>
            <StatusIndicator title={`SanMar: ${apiStatus.sanmar.message}`}>
              <StatusDot $status={apiStatus.sanmar.status} />
              <StatusLabel>SanMar: {apiStatus.sanmar.message}</StatusLabel>
            </StatusIndicator>
            {apiStatus.sanmar.sampleGarment?.sampleUnitPrice != null && (
              <SampleGarmentLine>
                Sample: {apiStatus.sanmar.sampleGarment.label} — ${Number(apiStatus.sanmar.sampleGarment.sampleUnitPrice).toFixed(2)}/unit
                {apiStatus.sanmar.sampleGarment.sampleSize && ` (${apiStatus.sanmar.sampleGarment.sampleSize})`}
              </SampleGarmentLine>
            )}
          </SanMarStatusBlock>
          <OutlineButton onClick={handleTestSanMarConnection} disabled={testingSanMar}>
            {testingSanMar ? 'Testing...' : 'Test SanMar'}
          </OutlineButton>
          <OutlineButton onClick={loadData}>
            🔄 Refresh
          </OutlineButton>
        </StatusIndicators>
      </PageHeader>

      <StatsBar>
        <StatCard>
          <h3>Total Lines</h3>
          <p>{stats.totalLines}</p>
        </StatCard>
        
        <StatCard>
          <h3>Selected</h3>
          <p>{stats.selectedCount}</p>
        </StatCard>
        
        <StatCard>
          <h3>SanMar Lines</h3>
          <p>{stats.sanMarCount}</p>
        </StatCard>
        
        <StatCard>
          <h3>S&S Lines</h3>
          <p>{stats.ssCount}</p>
        </StatCard>
        
        <StatCard>
          <h3>Total Pieces</h3>
          <p>{stats.totalPieces}</p>
        </StatCard>
        
        <SavingsCard>
          <h3>Estimated Total</h3>
          <p>${stats.estimatedTotal.toFixed(2)}</p>
        </SavingsCard>
      </StatsBar>

      <ControlsBar>
        <PrimaryButton 
          onClick={handlePlaceOrders}
          disabled={stats.selectedCount === 0}
        >
          📦 Place Orders ({stats.selectedCount} lines)
        </PrimaryButton>
        
        <SecondaryButton onClick={() => handleSelectAll(true)}>
          ✓ Select All
        </SecondaryButton>
        
        <OutlineButton onClick={() => handleSelectAll(false)}>
          ✗ Deselect All
        </OutlineButton>
      </ControlsBar>

      <TableContainer>
        <Table>
          <TableHeader>
            <tr>
              <TableHeaderCell style={{ width: '40px' }}>
                <Checkbox 
                  checked={stats.selectedCount === stats.totalLines && stats.totalLines > 0}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableHeaderCell>
              <TableHeaderCell>Customer</TableHeaderCell>
              <TableHeaderCell>Design</TableHeaderCell>
              <TableHeaderCell>Style</TableHeaderCell>
              <TableHeaderCell>Color</TableHeaderCell>
              <TableHeaderCell>Qty</TableHeaderCell>
              <TableHeaderCell>SanMar Price</TableHeaderCell>
              <TableHeaderCell>S&S Price</TableHeaderCell>
              <TableHeaderCell>Best Price</TableHeaderCell>
              <TableHeaderCell>Supplier</TableHeaderCell>
            </tr>
          </TableHeader>
          <tbody>
            {orders.map(order => {
              const lines = linesByOrder[order.id] || [];
              
              return lines.map((line, index) => {
                const comparison = priceComparisons[line.id];
                const selectedSupplier = supplierSelections[line.id];
                
                return (
                  <TableRow key={line.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedLines[line.id] || false}
                        onChange={(e) => setSelectedLines(prev => ({
                          ...prev,
                          [line.id]: e.target.checked
                        }))}
                      />
                    </TableCell>
                    
                    <TableCell>
                      {index === 0 && <strong>{order.customerName}</strong>}
                    </TableCell>
                    
                    <TableCell>{line.designName || 'N/A'}</TableCell>
                    <TableCell>{line.garmentId || 'N/A'}</TableCell>
                    <TableCell>{line.garmentColor || 'N/A'}</TableCell>
                    <TableCell><strong>{line.totalQuantity}</strong></TableCell>
                    
                    {comparison ? (
                      <>
                        <TableCell>
                          {comparison.suppliers?.sanmar?.total ? (
                            <PriceCell>
                              <Price isBest={comparison.bestSupplier === 'sanmar'}>
                                ${comparison.suppliers.sanmar.total.toFixed(2)}
                              </Price>
                              <PriceLabel>
                                ${comparison.suppliers.sanmar.unitPrice.toFixed(2)}/unit
                              </PriceLabel>
                            </PriceCell>
                          ) : (
                            <span style={{ color: '#888', fontSize: '0.85rem' }}>Not Found</span>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          {comparison.suppliers?.ssActivewear?.total ? (
                            <PriceCell>
                              <Price isBest={comparison.bestSupplier === 'ssActivewear'}>
                                ${comparison.suppliers.ssActivewear.total.toFixed(2)}
                              </Price>
                              <PriceLabel>
                                ${comparison.suppliers.ssActivewear.unitPrice.toFixed(2)}/unit
                              </PriceLabel>
                            </PriceCell>
                          ) : (
                            <span style={{ color: '#888', fontSize: '0.85rem' }}>Not Found</span>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          {comparison.bestPrice ? (
                            <PriceCell>
                              <strong>${comparison.bestPrice.toFixed(2)}</strong>
                              {comparison.savings > 0 && (
                                <SavingsTag>
                                  Save ${comparison.savings.toFixed(2)}
                                </SavingsTag>
                              )}
                            </PriceCell>
                          ) : (
                            <span style={{ color: '#ffd93d', fontSize: '0.85rem' }}>
                              Manual Order Required
                            </span>
                          )}
                        </TableCell>
                        
                        <TableCell>
                          <SupplierSelect
                            value={selectedSupplier || 'manual'}
                            onChange={(e) => setSupplierSelections(prev => ({
                              ...prev,
                              [line.id]: e.target.value
                            }))}
                          >
                            {comparison.suppliers?.sanmar?.total && (
                              <option value="sanmar">SanMar</option>
                            )}
                            {comparison.suppliers?.ssActivewear?.total && (
                              <option value="ssActivewear">S&S Activewear</option>
                            )}
                            <option value="manual">Manual Order</option>
                          </SupplierSelect>
                        </TableCell>
                      </>
                    ) : (
                      <TableCell colSpan={4}>Loading prices...</TableCell>
                    )}
                  </TableRow>
                );
              });
            })}
          </tbody>
        </Table>
      </TableContainer>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <ModalOverlay onClick={() => !placingOrder && setShowConfirmModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Ready to place orders:</ModalTitle>
            
            <ModalText>SanMar: {stats.sanMarCount} lines</ModalText>
            <ModalText>S&S Activewear: {stats.ssCount} lines</ModalText>
            <ModalText>Manual Orders: {stats.manualCount} lines</ModalText>
            
            <ModalText className="highlight" style={{ marginTop: '20px' }}>
              Total Pieces: {stats.totalPieces}
            </ModalText>
            <ModalText className="highlight">
              Estimated Total: ${stats.estimatedTotal.toFixed(2)}
            </ModalText>
            
            <ModalText style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.7)', marginTop: '16px' }}>
              (API orders will be placed automatically - manual orders will need to be placed separately)
            </ModalText>
            
            <ModalActions>
              <OutlineButton 
                onClick={() => setShowConfirmModal(false)}
                disabled={placingOrder}
              >
                Cancel
              </OutlineButton>
              <PrimaryButton 
                onClick={confirmPlaceOrders}
                disabled={placingOrder}
              >
                {placingOrder ? '🔄 Placing Orders...' : '📦 Confirm & Place Orders'}
              </PrimaryButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default APIOrdering;

